<template>
  <div v-redirect-if-no-permission:MANAGE_FINANCE_JOURNALS class="fin-page">
    <finance-section-nav />
    <loading-component :value="loading" />

    <!-- Close: informative, not alarming. Closing is the safe direction. -->
    <v-dialog v-model="closeDialog" max-width="640px" persistent>
      <v-card>
        <v-card-title>
          {{ $t("finance.periodLocks.closeTitle", { month: closeForm.month }) }}
          <v-spacer></v-spacer>
          <v-btn icon @click="closeCloseDialog"><v-icon>close</v-icon></v-btn>
        </v-card-title>
        <v-card-text>
          <div class="pl-notice pl-notice--warn">{{ $t("finance.periodLocks.closeWarning") }}</div>
          <div class="pl-notice">{{ $t("finance.periodLocks.closeOrderHint") }}</div>
          <v-text-field
            v-model="closeForm.note"
            :label="$t('finance.periodLocks.closeNote')"
            maxlength="255"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat @click="closeCloseDialog">{{ $t("finance.common.cancel") }}</v-btn>
          <v-btn color="primary" :loading="closing" @click="submitClose">
            {{ $t("finance.periodLocks.closeConfirm") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Reopen: the dangerous direction. It is what lets an issued statement
         change, so it asks for a written reason AND the month typed back. -->
    <v-dialog v-model="reopenDialog" max-width="640px" persistent>
      <v-card>
        <v-card-title class="error--text">
          {{ $t("finance.periodLocks.reopenTitle", { month: reopenForm.month }) }}
          <v-spacer></v-spacer>
          <v-btn icon @click="closeReopenDialog"><v-icon>close</v-icon></v-btn>
        </v-card-title>
        <v-card-text>
          <div class="pl-notice pl-notice--danger">{{ $t("finance.periodLocks.reopenWarning") }}</div>
          <div class="pl-notice">{{ $t("finance.periodLocks.reopenSnapshotNote") }}</div>
          <v-text-field
            v-model="reopenForm.note"
            :label="$t('finance.periodLocks.reopenReason')"
            maxlength="255"
          ></v-text-field>
          <v-text-field
            v-model="reopenForm.confirmation"
            :label="$t('finance.periodLocks.reopenTypeToConfirm', { month: reopenForm.month })"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat @click="closeReopenDialog">{{ $t("finance.common.cancel") }}</v-btn>
          <v-btn color="error" :loading="reopening" :disabled="!canReopen" @click="submitReopen">
            {{ $t("finance.periodLocks.reopenConfirm") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-card>
      <div class="fin-header fin-header--sticky">
        <div class="fin-toolbar">
          <span class="fin-title">{{ $t("finance.periodLocks.title") }}</span>
          <v-spacer></v-spacer>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat color="primary" @click="loadLocks">
              <v-icon small>refresh</v-icon>
            </v-btn>
            <span>{{ $t("finance.common.refresh") }}</span>
          </v-tooltip>
        </div>
        <div class="fin-filters">
          <v-select
            v-model="monthsToShow"
            :items="monthCountOptions"
            item-text="text"
            item-value="value"
            :label="$t('finance.periodLocks.monthsToShow')"
            hide-details
            class="fin-field"
            @change="loadLocks"
          ></v-select>
        </div>
      </div>

      <div class="fin-summary">
        <div class="fin-stat">
          <span class="fin-stat-label">{{ $t("finance.periodLocks.openCount") }}</span>
          <span class="fin-stat-value">{{ openCount }}</span>
        </div>
        <div class="fin-stat">
          <span class="fin-stat-label">{{ $t("finance.periodLocks.closedCount") }}</span>
          <span class="fin-stat-value">{{ closedCount }}</span>
        </div>
      </div>

      <v-data-table
        class="fin-dt"
        :headers="headers"
        :items="months"
        :rows-per-page-items="[12, 24, 36, 60]"
        :no-data-text="$t('finance.periodLocks.noMonths')"
      >
        <template v-slot:items="props">
          <tr>
            <td class="pl-mono">{{ props.item.month }}</td>
            <td>
              <v-chip small :color="props.item.status === 'CLOSED' ? 'error' : 'success'" text-color="white">
                {{ props.item.status === "CLOSED" ? $t("finance.periodLocks.closed") : $t("finance.periodLocks.open") }}
              </v-chip>
            </td>
            <td>{{ props.item.closed_by || "-" }}</td>
            <td class="pl-mono">{{ formatDateTime(props.item.closed_at) }}</td>
            <td>{{ props.item.reopened_by || "-" }}</td>
            <td class="pl-mono">{{ formatDateTime(props.item.reopened_at) }}</td>
            <td>{{ props.item.note || "-" }}</td>
            <td class="fin-num">{{ props.item.snapshots }}</td>
            <td>
              <v-btn
                v-if="props.item.status !== 'CLOSED' && props.item.is_closable"
                small
                color="primary"
                @click.stop="openCloseDialog(props.item)"
              >
                {{ $t("finance.periodLocks.close") }}
              </v-btn>
              <span v-else-if="props.item.status !== 'CLOSED'" class="caption grey--text">
                {{ $t("finance.periodLocks.notClosable") }}
              </span>
              <v-btn v-else small color="error" outline @click.stop="openReopenDialog(props.item)">
                {{ $t("finance.periodLocks.reopen") }}
              </v-btn>
            </td>
          </tr>
        </template>
      </v-data-table>
    </v-card>

    <!-- What the last close actually froze. A snapshot failure never aborts a
         close, so the only place it can surface is here; it must not be a
         silent absence from the archive. -->
    <v-card v-if="lastClose" class="mt-2">
      <div class="fin-header">
        <div class="fin-toolbar">
          <span class="fin-title">
            {{ $t("finance.periodLocks.snapshotResult", { month: lastClose.month }) }}
          </span>
          <v-spacer></v-spacer>
          <v-btn icon small flat @click="lastClose = null"><v-icon small>close</v-icon></v-btn>
        </div>
      </div>
      <div class="fin-summary">
        <div class="fin-stat">
          <span class="fin-stat-label">{{ $t("finance.periodLocks.captured") }}</span>
          <span class="fin-stat-value">{{ lastClose.captured.length }}</span>
        </div>
        <div class="fin-stat">
          <span class="fin-stat-label">{{ $t("finance.periodLocks.failedSnapshots") }}</span>
          <span :class="lastClose.failed.length ? 'fin-stat-bad' : 'fin-stat-value'">
            {{ lastClose.failed.length }}
          </span>
        </div>
        <div class="fin-stat">
          <span class="fin-stat-label">{{ $t("finance.periodLocks.skipped") }}</span>
          <span class="fin-stat-value">{{ lastClose.skipped.length }}</span>
        </div>
      </div>

      <div v-for="warning in lastClose.warnings" :key="warning.code" class="fin-alert-error">
        <v-icon small color="#c62828">warning</v-icon>
        {{ warning.ar }}
      </div>

      <div v-if="lastClose.earlierOpen.length" class="fin-alert-error">
        <v-icon small color="#c62828">warning</v-icon>
        {{ $t("finance.periodLocks.earlierOpenHint") }}
        ({{ $t("finance.periodLocks.earlierOpenMonths") }}: {{ lastClose.earlierOpen.join(", ") }})
      </div>

      <v-list v-if="lastClose.failed.length" dense>
        <v-list-tile v-for="item in lastClose.failed" :key="'f-' + item.slug">
          <v-list-tile-content>
            <v-list-tile-title class="error--text">{{ item.title_ar || item.slug }}</v-list-tile-title>
            <v-list-tile-sub-title>{{ item.error }}</v-list-tile-sub-title>
          </v-list-tile-content>
        </v-list-tile>
      </v-list>

      <v-list v-if="lastClose.skipped.length" dense>
        <v-list-tile v-for="item in lastClose.skipped" :key="'s-' + item.slug">
          <v-list-tile-content>
            <v-list-tile-title>{{ item.title_ar || item.slug }}</v-list-tile-title>
            <v-list-tile-sub-title>{{ item.reason_ar }}</v-list-tile-sub-title>
          </v-list-tile-content>
        </v-list-tile>
      </v-list>
    </v-card>

    <v-card class="mt-2">
      <v-expansion-panel>
        <v-expansion-panel-content>
          <div slot="header" class="fin-title">
            {{ $t("finance.periodLocks.snapshotCatalog") }} ({{ snapshotReports.length }})
          </div>
          <div class="pl-notice">{{ $t("finance.periodLocks.snapshotCatalogHint") }}</div>
          <v-data-table
            class="fin-dt"
            :headers="catalogHeaders"
            :items="snapshotReports"
            :rows-per-page-items="[10, 25, 50]"
          >
            <template v-slot:items="props">
              <tr>
                <td class="pl-mono">{{ props.item.slug }}</td>
                <td>{{ props.item.title_ar }}</td>
                <td>{{ props.item.family }}</td>
                <td>{{ props.item.status }}</td>
              </tr>
            </template>
          </v-data-table>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-card>
  </div>
</template>

<script>
import { formatFinanceDate } from "@/mixins/financeDate";
import FinanceSectionNav from "@/components/finance/FinanceSectionNav";
import LoadingComponent from "@/components/LoadingComponent";

export default {
  name: "PeriodLocks",
  title() {
    return this.$t("finance.periodLocks.pageTitle");
  },
  components: { FinanceSectionNav, LoadingComponent },
  data() {
    return {
      loading: false,
      closing: false,
      reopening: false,
      closeDialog: false,
      reopenDialog: false,
      monthsToShow: 24,
      months: [],
      snapshotReports: [],
      // The outcome of the close performed in THIS session. Kept on screen
      // until dismissed because a report that could not be frozen is reported
      // once, in the close response, and nowhere else.
      lastClose: null,
      closeForm: { month: null, note: null },
      reopenForm: { month: null, note: null, confirmation: null },
    };
  },
  computed: {
    headers() {
      return [
        { text: this.$t("finance.periodLocks.month"), value: "month" },
        { text: this.$t("finance.periodLocks.status"), value: "status" },
        { text: this.$t("finance.periodLocks.closedBy"), value: "closed_by" },
        { text: this.$t("finance.periodLocks.closedAt"), value: "closed_at" },
        { text: this.$t("finance.periodLocks.reopenedBy"), value: "reopened_by" },
        { text: this.$t("finance.periodLocks.reopenedAt"), value: "reopened_at" },
        { text: this.$t("finance.periodLocks.note"), value: "note" },
        { text: this.$t("finance.periodLocks.snapshots"), value: "snapshots", align: "end" },
        { text: this.$t("finance.common.actions"), value: "actions", sortable: false },
      ];
    },
    catalogHeaders() {
      return [
        { text: this.$t("finance.common.code"), value: "slug" },
        { text: this.$t("finance.common.name"), value: "title_ar" },
        { text: this.$t("finance.common.type"), value: "family" },
        { text: this.$t("finance.common.status"), value: "status" },
      ];
    },
    monthCountOptions() {
      return [12, 24, 36, 60].map(function (count) {
        return { text: String(count), value: count };
      });
    },
    openCount() {
      return this.months.filter(function (row) {
        return row.status !== "CLOSED";
      }).length;
    },
    closedCount() {
      return this.months.filter(function (row) {
        return row.status === "CLOSED";
      }).length;
    },
    // Both gates, not either: the reason is the audit record, the typed month
    // is what stops a reopen being one mis-click on the wrong row.
    canReopen() {
      return (
        !!this.reopenForm.note &&
        String(this.reopenForm.note).trim().length > 0 &&
        String(this.reopenForm.confirmation || "").trim() === this.reopenForm.month
      );
    },
  },
  mounted() {
    this.loadLocks();
  },
  methods: {
    getErrorMessage(error, fallback) {
      if (error && error.response && error.response.data && error.response.data.message) {
        return error.response.data.message;
      }

      return fallback;
    },
    loadLocks() {
      this.loading = true;
      this.axios
        .get(this.api.ACTIONS.ACCOUNTING_PERIOD_LOCKS, { params: { months: this.monthsToShow } })
        .then(
          function (response) {
            var payload = (response.data && response.data.data) || {};
            this.months = payload.months || [];
            this.snapshotReports = payload.snapshot_reports || [];
          }.bind(this)
        )
        .catch(
          function (error) {
            window.getApp.$emit(
              "SHOW_TOAST",
              this.getErrorMessage(error, this.$t("finance.periodLocks.failedLoad")),
              "red"
            );
          }.bind(this)
        )
        .finally(
          function () {
            this.loading = false;
          }.bind(this)
        );
    },
    openCloseDialog(row) {
      this.closeForm = { month: row.month, note: null };
      this.closeDialog = true;
    },
    closeCloseDialog() {
      this.closeDialog = false;
      this.closeForm = { month: null, note: null };
    },
    submitClose() {
      if (!this.closeForm.month) {
        return;
      }

      this.closing = true;
      this.axios
        .post(this.api.ACTIONS.ACCOUNTING_PERIOD_LOCKS, {
          month: this.closeForm.month,
          note: this.closeForm.note,
        })
        .then(
          function (response) {
            var payload = (response.data && response.data.data) || {};
            var snapshots = payload.snapshots || {};
            this.lastClose = {
              month: this.closeForm.month,
              captured: snapshots.captured || [],
              failed: snapshots.failed || [],
              skipped: snapshots.skipped || [],
              warnings: payload.warnings || [],
              earlierOpen: payload.earlier_open_months || [],
            };
            window.getApp.$emit("SHOW_TOAST", this.$t("finance.periodLocks.closeDone"), "green");
            this.closeCloseDialog();
            this.loadLocks();
          }.bind(this)
        )
        .catch(
          function (error) {
            window.getApp.$emit(
              "SHOW_TOAST",
              this.getErrorMessage(error, this.$t("finance.periodLocks.failedClose")),
              "red"
            );
          }.bind(this)
        )
        .finally(
          function () {
            this.closing = false;
          }.bind(this)
        );
    },
    openReopenDialog(row) {
      this.reopenForm = { month: row.month, note: null, confirmation: null };
      this.reopenDialog = true;
    },
    closeReopenDialog() {
      this.reopenDialog = false;
      this.reopenForm = { month: null, note: null, confirmation: null };
    },
    submitReopen() {
      if (!this.canReopen) {
        window.getApp.$emit(
          "SHOW_TOAST",
          this.$t(
            this.reopenForm.note
              ? "finance.periodLocks.reopenTypeMismatch"
              : "finance.periodLocks.reopenReasonRequired"
          ),
          "red"
        );
        return;
      }

      this.reopening = true;
      // The reason travels in the body of a DELETE, which is what the endpoint
      // requires and how this dashboard already sends a cancellation reason.
      this.axios
        .delete(this.api.ACTIONS.ACCOUNTING_PERIOD_LOCK(this.reopenForm.month), {
          data: { note: this.reopenForm.note },
        })
        .then(
          function (response) {
            var payload = (response.data && response.data.data) || {};
            var notes = payload.notes || [];
            window.getApp.$emit("SHOW_TOAST", this.$t("finance.periodLocks.reopenDone"), "green");
            // The server's own note explains that the snapshots survive; show
            // it rather than a paraphrase, so the two can never disagree.
            if (notes.length && notes[0].ar) {
              window.getApp.$emit("SHOW_TOAST", notes[0].ar, "orange");
            }
            this.closeReopenDialog();
            this.loadLocks();
          }.bind(this)
        )
        .catch(
          function (error) {
            window.getApp.$emit(
              "SHOW_TOAST",
              this.getErrorMessage(error, this.$t("finance.periodLocks.failedReopen")),
              "red"
            );
          }.bind(this)
        )
        .finally(
          function () {
            this.reopening = false;
          }.bind(this)
        );
    },
    formatDateTime(value) {
      // One shared finance formatter (accountant doc 2026-10-03 item 1.8).
      return formatFinanceDate(value);
    },
  },
};
</script>

<style scoped>
.pl-notice {
  font-size: 13px;
  line-height: 1.6;
  margin-bottom: 10px;
}
.pl-notice--warn {
  padding: 8px 12px;
  border-radius: 4px;
  background: #fff8e1;
  border-inline-start: 4px solid #f9a825;
  font-weight: 600;
}
.pl-notice--danger {
  padding: 8px 12px;
  border-radius: 4px;
  background: #ffebee;
  color: #b71c1c;
  border-inline-start: 4px solid #c62828;
  font-weight: 700;
}
/* Dates and counts read left-to-right even inside the Arabic layout. */
.pl-mono {
  direction: ltr;
  unicode-bidi: embed;
  white-space: nowrap;
}
</style>
